import { styled, keyframes } from '@mui/system';
import { Box, Typography, Button, Grid, IconButton, Skeleton } from '@mui/material';
import { fontFamilies, colors } from '../../../../../constants/theme';

export { HeroSection, CarouselContainer } from '../../styles';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const NavigationButtonBase = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  color: colors.primary,
  zIndex: 10,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
  },
  '&.Mui-disabled': {
    color: '#999',
  },
  [theme.breakpoints.down('md')]: {
    width: '40px',
    height: '40px',
    '& svg': {
      fontSize: '20px',
    },
  },
}));

export const PrevNavigationButton = styled(NavigationButtonBase)(({ theme }) => ({
  left: 10,
  [theme.breakpoints.up(1060)]: {
    left: -60,
  },
}));

export const NextNavigationButton = styled(NavigationButtonBase)(({ theme }) => ({
  right: 10,
  [theme.breakpoints.up(1060)]: {
    right: -60,
  },
}));

export const ContentWrapper = styled(Box)(({ theme }) => ({
  maxWidth: '1150px',
  width: '100%',
  margin: '0 auto',
  padding: '0',
  [theme.breakpoints.down('md')]: {
    padding: '0 20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0 16px',
  },
}));

export const TextContent = styled(Box)(({ theme }) => ({
  marginBottom: '60px',
  display: 'flex',
  flexDirection: 'column',
  [theme.breakpoints.up('md')]: {
    alignItems: 'flex-start',
  },
  [theme.breakpoints.down('md')]: {
    alignItems: 'start',
  },
}));

export const Heading = styled(Typography)(({ theme }) => ({
  ...theme.typography.heading,
  textAlign: 'start',
  fontSize: '60px',
  letterSpacing: '-1.5px',
  lineHeight: '73px',
  [theme.breakpoints.down('md')]: {
    fontSize: '40px',
    fontWeight: 300,
    lineHeight: '44px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '25px',
    fontWeight: 300,
    lineHeight: '44px',
    textAlign: 'center',
    margin: '0 auto',
  },
  marginBottom: '24px',
}));

export const Subtitle = styled(Box)(({ theme }) => ({
  ...theme.typography.subtext,
  [theme.breakpoints.down('md')]: {
    fontSize: '20px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '40px',
  },
  letterSpacing: '-3px',
}));

export const SubtitleAnimated = styled(Subtitle)(({ theme }) => ({
  width: 'min-content',
  fontWeight: 600,
  letterSpacing: '.5px',

  [theme.breakpoints.down('sm')]: {
    width: '100%',

    '& .text-transition': {
      display: 'flex !important',
      justifyContent: 'center',
      width: '100% !important',
    },
  },
}));

export const AnimatedTextContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    textAlign: 'center',
    margin: '0 auto',
  },
}));

export const Description = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtext,
  color: colors.primary,
  lineHeight: '100%',
  letterSpacing: '.48px',
  marginTop: '20px',
  marginBottom: '37px',
  textAlign: 'start',

  [theme.breakpoints.down('md')]: {
    fontSize: '18px',
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '24px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '90%',
    textAlign: 'center',
    marginLeft: 'auto',
    marginRight: 'auto',
  },
}));

export const HiddenMeasure = styled('span')({
  position: 'absolute',
  visibility: 'hidden',
  height: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  fontWeight: 500,
  letterSpacing: '.5px',
});

export const AnimatedTextWrapper = styled('span')({
  display: 'inline-block',
  overflow: 'hidden',
  verticalAlign: 'bottom',
  position: 'relative',
  transition: 'width 0.3s ease',
});

export const AnimatedText = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isAnimating',
})(({ isAnimating }) => ({
  display: 'inline-block',
  fontWeight: 500,
  letterSpacing: '.5px',
  whiteSpace: 'nowrap',
  transition: 'transform 0.3s ease, opacity 0.3s ease',
  transform: isAnimating ? 'translateY(-100%)' : 'translateY(0)',
  opacity: isAnimating ? 0 : 1,
}));

export const LightText = styled('span')({
  color: colors.primary,
  fontWeight: 400,
  letterSpacing: '-.8px',
});

export const ButtonWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '20px',
  marginTop: '23px',
  justifyContent: 'flex-start',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    margin: 'auto',
  },
}));

export const SecondaryButton = styled(Button)({
  color: colors.secondary,
  borderColor: colors.secondary,
  fontFamily: fontFamilies.quicksand,
  fontSize: '16px',
  fontWeight: 600,
  padding: '12px 30px',
  textTransform: 'none',
  borderRadius: '4px',
  '&:hover': {
    borderColor: '#1f3147',
    backgroundColor: 'rgba(41, 64, 90, 0.04)',
  },
});

export const StatusText = styled(Typography)({
  marginTop: '32px',
  color: colors.primary,
  fontSize: '20px',
});

export const CardsContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  overflow: 'visible',
  [theme.breakpoints.down('md')]: {
    paddingLeft: '8px',
    paddingRight: '8px',
    overflow: 'hidden',
  },
}));

export const CardsGrid = styled(Grid)(({ theme }) => ({
  flexWrap: 'nowrap',
  alignItems: 'stretch',
  justifyContent: 'center',
  '&::-webkit-scrollbar': { display: 'none' },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
  [theme.breakpoints.down('md')]: {
    overflowX: 'auto',
  },
  [theme.breakpoints.up('md')]: {
    overflowX: 'visible',
  },
}));

export const CardWrapper = styled(Grid)({
  display: 'flex',
  flexShrink: 0,
  width: '270px',
  borderRadius: '16px',
});

export const CardContent = styled(Box)(({ theme }) => ({
  textAlign: 'left',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  position: 'relative',
  borderRadius: '16px',
  boxShadow: '4px 4px 8px 0px #00000012',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.1)',
    zIndex: 10,
  },
  '&:hover .card-link': {
    opacity: 1,
  },

  [theme.breakpoints.down('sm')]: {
    textAlign: 'center',
  },
}));

export const CardImage = styled('img')({
  width: '270px',
  height: '330px',
  objectFit: 'cover',
  border: 'none',
  display: 'block',
  flexShrink: 0,
  borderRadius: '16px 15px 0 0',
  background: colors.background,
  objectPosition: 'top',
  animation: `${fadeIn} 0.5s ease forwards`,
});

export const CardImageSkeleton = styled(Skeleton)({
  width: '270px',
  height: '330px',
  borderRadius: '16px 15px 0 0',
  transform: 'none',
});

export const CardNameSkeleton = styled(Skeleton)({
  width: '140px',
  height: '24px',
});

export const CardSpecialtySkeleton = styled(Skeleton)({
  width: '200px',
  height: '48px',
});

export const CardName = styled(Typography)({
  fontFamily: fontFamilies.gotham,
  fontSize: '20px',
  fontWeight: 500,
  color: colors.primary,
  lineHeight: '24px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
  maxWidth: '225px',
});

export const CardSpecialty = styled(Typography)({
  fontFamily: fontFamilies.quicksand,
  fontSize: '15px',
  fontWeight: 400,
  color: colors.primary,
  height: '48px',
  maxWidth: '225px',
  display: '-webkit-box',
  WebkitLineClamp: 4,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  minWidth: 0,
  lineHeight: '24px',
});

export const CardInfoWrapper = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  padding: '23px 16px',
  gap: '10px',
  background: '#E6EBF0',
  borderRadius: '0 0 16px 16px',
});

export const CardLink = styled(Button)(({ theme }) => ({
  ...theme.typography.gotham,
  fontSize: '15px',
  lineHeight: '24px',
  fontWeight: 500,
  display: 'inline-block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  position: 'absolute',
  bottom: '150px',
  textDecoration: 'none',
  color: colors.text,
  background: colors.whiteText,
  left: '20px',
  right: '20px',
  opacity: 0,
  borderRadius: '8px',
  transition: 'all 0.3s ease ',
  '&.MuiButtonBase-root': {
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
    '&:focus-visible': {
      outline: 'none',
      boxShadow: 'none',
    },
  },
  '& .MuiTouchRipple-root': {
    display: 'none',
  },
  '&:hover': {
    transform: 'scale(1.04)',
    background: colors.whiteText,
  },
  '&:active': {
    background: colors.calmBlue,
    color: colors.whiteText,
  },
}));
